import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Zap, Wallet, ArrowRight, Flame, Gamepad2, Snowflake, Apple, BookOpen, ChevronRight, GitCompare, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import DeviceCard from "@/components/DeviceCard";
import { fetchDevices, fetchGuides } from "@/lib/api";
import { BUDGET_BANDS, USE_CASES, COMPARISONS, GLOSSARY, BRANDS } from "@/data/static";

const HERO = "https://images.unsplash.com/photo-1564049489314-60d154ff107d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ICONS = { "gamepad-2": Gamepad2, apple: Apple, snowflake: Snowflake, wallet: Wallet };

export default function Home() {
  const [devices, setDevices] = useState([]);
  const [guides, setGuides] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchDevices({ sort: "popularity" }).then(setDevices).catch(() => {});
    fetchGuides().then(setGuides).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/fps-finder?q=${encodeURIComponent(q)}`);
  };

  const trending = devices.slice(0, 6);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "GamingKits.online",
    url: "https://gamingkits.online",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://gamingkits.online/fps-finder?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <Seo
        title="Pakistan's Gaming Phone Database"
        description="Compare gaming smartphones in Pakistan by budget, FPS and condition. Discover used-kit and box-pack phones, check reported gaming performance and explore trusted buying options."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 cyber-grid opacity-60" />
        <img src={HERO} alt="Esports gamer holding a gaming smartphone" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/85 to-charcoal" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-3 py-1 text-xs font-heading font-semibold text-neon">
              <Flame className="h-3.5 w-3.5" /> Made for Pakistani gamers
            </span>
            <h1 className="mt-5 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-6xl">
              Unlock <span className="text-neon drop-shadow-[0_0_18px_rgba(57,255,20,0.5)]">90 FPS</span>:<br />
              Pakistan's Gaming Phone Database
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
              Compare gaming smartphones by budget, condition and likely gaming performance. Understand used-kit vs
              box-pack options - then explore trusted buying links.
            </p>

            <form onSubmit={submit} className="mt-8 max-w-xl">
              <div className="flex items-center gap-2 rounded-xl border border-border bg-graphite/90 p-2 backdrop-blur focus-within:border-neon/50">
                <Search className="ml-2 h-5 w-5 text-slate-500" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search phone, chipset or game (e.g. OnePlus, 8 Gen 1)…"
                  className="w-full bg-transparent px-2 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none sm:text-base"
                />
                <button data-testid="hero-search-btn" type="submit" className="rounded-lg bg-neon px-4 py-2.5 text-sm font-heading font-bold text-charcoal transition-all hover:shadow-[0_0_18px_rgba(57,255,20,0.5)]">
                  Search
                </button>
              </div>
            </form>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link data-testid="cta-fps-finder" to="/fps-finder" className="inline-flex items-center gap-2 rounded-lg bg-neon px-5 py-3 text-sm font-heading font-bold text-charcoal transition-all hover:shadow-[0_0_22px_rgba(57,255,20,0.5)]">
                <Zap className="h-4 w-4" /> Explore FPS Finder
              </Link>
              <Link data-testid="cta-browse-budget" to="/price-index" className="inline-flex items-center gap-2 rounded-lg border border-cyber/40 bg-cyber/10 px-5 py-3 text-sm font-heading font-bold text-cyber transition-all hover:bg-cyber/20">
                <Wallet className="h-4 w-4" /> Browse by Budget
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* BUDGET HOTLINKS */}
        <section className="py-12 sm:py-16">
          <SectionHead title="Shop by Budget" sub="Jump straight to your price band in PKR" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {BUDGET_BANDS.map((b) => (
              <Link
                key={b.slug}
                data-testid={`budget-card-${b.slug}`}
                to={`/category/${b.slug}`}
                className={`group relative overflow-hidden rounded-xl border bg-slateSurface p-5 transition-all duration-300 hover:-translate-y-1 ${
                  b.accent === "green" ? "border-border hover:border-neon/60 hover:shadow-[0_0_22px_rgba(57,255,20,0.15)]" : "border-border hover:border-cyber/60 hover:shadow-[0_0_22px_rgba(0,163,255,0.15)]"
                }`}
              >
                <Wallet className={`h-6 w-6 ${b.accent === "green" ? "text-neon" : "text-cyber"}`} />
                <h3 className="mt-4 font-heading text-base font-bold text-white sm:text-lg">{b.label}</h3>
                <p className="mt-1 text-xs text-slate-400">{b.sub}</p>
                <ArrowRight className="mt-4 h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-1 group-hover:text-white" />
              </Link>
            ))}
          </div>
        </section>

        {/* TRENDING */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Trending Now" sub="Popular gaming phones right now" link="/fps-finder" />
          <div className="flex snap-x gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {trending.map((d) => (
              <div key={d.slug} className="w-64 shrink-0 snap-start sm:w-72">
                <DeviceCard device={d} />
              </div>
            ))}
            {trending.length === 0 && <SkeletonRow />}
          </div>
        </section>

        {/* USE CASES */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Popular Use-Cases" sub="Find phones for how you actually play" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASES.map((u) => {
              const Icon = ICONS[u.icon] || Gamepad2;
              return (
                <Link
                  key={u.slug}
                  data-testid={`usecase-${u.slug}`}
                  to={`/category/${u.slug}`}
                  className="group rounded-xl border border-border bg-slateSurface p-5 transition-all hover:-translate-y-1 hover:border-cyber/50"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyber/10 text-cyber ring-1 ring-cyber/30">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-heading text-sm font-bold text-white">{u.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-400">{u.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-cyber">
                    View picks <ChevronRight className="h-3 w-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* CONVERSION BANNER */}
        <section className="py-8 sm:py-12">
          <div className="relative overflow-hidden rounded-2xl border border-neon/30 bg-graphite p-6 sm:p-10">
            <div className="absolute inset-0 cyber-grid opacity-40" />
            <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <h2 className="font-heading text-xl font-bold text-white sm:text-2xl">Avoid risky deals.</h2>
                <p className="mt-2 text-sm text-slate-300 sm:text-base">
                  Compare gaming phones, understand condition differences (used-kit, box-pack, refurbished) and explore
                  trusted buying options with clear, honest information.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link data-testid="banner-cta-finder" to="/fps-finder" className="inline-flex items-center gap-2 rounded-lg bg-neon px-5 py-3 text-sm font-heading font-bold text-charcoal transition-all hover:shadow-[0_0_20px_rgba(57,255,20,0.5)]">
                  Compare Phones
                </Link>
                <Link data-testid="banner-cta-guides" to="/guides" className="inline-flex items-center gap-2 rounded-lg border border-cyber/40 bg-cyber/10 px-5 py-3 text-sm font-heading font-bold text-cyber transition-all hover:bg-cyber/20">
                  Read Buying Guides
                </Link>
                <a
                  data-testid="banner-cta-phoneflip"
                  href="https://phoneflip.pk/used-phones"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-500/40 bg-slateSurface px-5 py-3 text-sm font-heading font-bold text-white transition-all hover:border-neon/50"
                >
                  Buy on PhoneFlip <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED COMPARISONS */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Featured Comparisons" sub="Head-to-head gaming value breakdowns" link="/compare" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {COMPARISONS.map((c) => (
              <Link
                key={c.title}
                data-testid={`comparison-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
                to={`/compare/${c.a}/${c.b}`}
                className="group rounded-xl border border-border bg-slateSurface p-5 transition-all hover:-translate-y-1 hover:border-neon/50"
              >
                <GitCompare className="h-5 w-5 text-neon" />
                <h3 className="mt-3 font-heading text-sm font-bold text-white">{c.title}</h3>
                <p className="mt-1 text-xs text-slate-400">{c.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* SHOP BY BRAND */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Shop by Brand" sub="Browse gaming phones from your favourite brand" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {Object.entries(BRANDS).map(([slug, b]) => (
              <Link
                key={slug}
                data-testid={`brand-card-${slug}`}
                to={`/brand/${slug}`}
                className="rounded-xl border border-border bg-slateSurface p-4 text-center transition-all hover:-translate-y-1 hover:border-cyber/50"
              >
                <span className="font-heading text-sm font-bold text-white">{b.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* LATEST GUIDES */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Guides" sub="Authority content on gaming phones in Pakistan" link="/guides" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {guides.slice(0, 3).map((g) => (
              <Link
                key={g.slug}
                data-testid={`home-guide-${g.slug}`}
                to={`/guides/${g.slug}`}
                className="group overflow-hidden rounded-xl border border-border bg-slateSurface transition-all hover:-translate-y-1 hover:border-cyber/50"
              >
                <div className="aspect-[16/9] overflow-hidden bg-graphite">
                  <img src={g.cover_image} alt={g.title} loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <BookOpen className="h-3.5 w-3.5" /> {g.read_minutes} min read
                  </div>
                  <h3 className="mt-2 font-heading text-sm font-bold text-white group-hover:text-cyber">{g.title}</h3>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-400">{g.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* GLOSSARY PREVIEW */}
        <section className="py-8 sm:py-12">
          <SectionHead title="Gamer's Glossary" sub="Know the terms before you buy" link="/glossary" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {GLOSSARY.slice(0, 6).map((g) => (
              <Link key={g.term} to="/glossary" className="rounded-lg border border-border bg-slateSurface p-4 transition-colors hover:border-neon/40">
                <div className="font-heading text-sm font-semibold text-neon">{g.term}</div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-400">{g.def}</p>
              </Link>
            ))}
          </div>
        </section>

        <div className="pb-12">
          <Disclaimer />
        </div>
      </div>
    </>
  );
}

const SectionHead = ({ title, sub, link }) => (
  <div className="mb-6 flex items-end justify-between gap-4">
    <div>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h2>
      {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
    </div>
    {link && (
      <Link to={link} className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-neon hover:underline sm:inline-flex">
        View all <ChevronRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

const SkeletonRow = () =>
  Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="h-80 w-64 shrink-0 animate-pulse rounded-xl border border-border bg-slateSurface" />
  ));
